import React, { useState, useRef, useEffect } from 'react';

const EditableText = ({
  text,
  onSave,
  renderLatex = true,
  className = '',
  placeholder = 'Enter text...',
  multiline = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(text || '');
  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();

      // If we have a saved cursor position, restore it
      if (cursorPositionRef.current !== null) {
        const { selectionStart, selectionEnd } = cursorPositionRef.current;
        inputRef.current.setSelectionRange(selectionStart, selectionEnd);
      } else {
        // Otherwise, position cursor at the end of text (initial focus only)
        const length = editedText ? editedText.length : 0;
        inputRef.current.setSelectionRange(length, length);
      }
    }
  }, [isEditing]);

  useEffect(() => {
    setEditedText(text || '');
  }, [text]);

  // Process <VISUAL> tags for display in the edit field
  const getFormattedEditText = () => {
    if (!editedText) {
      return '';
    }

    // Detect visual tags
    const visualRegex = /<VISUAL>([\s\S]*?)<\/VISUAL>/g;
    let formattedText = editedText;

    // Replace visual tags with formatted versions
    const matches = [...editedText.matchAll(visualRegex)];
    // Process matches in reverse order to avoid position shifts
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      const fullMatch = match[0];
      const visualContent = match[1];
      const startIndex = match.index;

      const replacement = `[Visual Description: ${visualContent}]`;

      formattedText =
        formattedText.substring(0, startIndex) +
        replacement +
        formattedText.substring(startIndex + fullMatch.length);
    }

    return formattedText;
  };

  // Function to convert formatted visual descriptions back to proper tags
  const convertFormattedToTags = formatted => {
    if (!formatted) {
      return '';
    }

    const formattedRegex = /\[Visual Description: ([\s\S]*?)\]/g;
    let result = formatted;

    // Replace formatted visual descriptions back to tags
    const matches = [...formatted.matchAll(formattedRegex)];
    // Process matches in reverse order to avoid position shifts
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      const fullMatch = match[0];
      const visualContent = match[1];
      const startIndex = match.index;

      // Create a replacement with the original tag format
      const replacement = `<VISUAL>${visualContent}</VISUAL>`;

      result =
        result.substring(0, startIndex) +
        replacement +
        result.substring(startIndex + fullMatch.length);
    }

    return result;
  };

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);

    // Convert any formatted visual descriptions back to proper tags
    const convertedText = convertFormattedToTags(editedText);

    if (convertedText !== text) {
      onSave(convertedText);
    }
  };

  const handleChange = e => {
    // Save the current cursor position
    cursorPositionRef.current = {
      selectionStart: e.target.selectionStart,
      selectionEnd: e.target.selectionEnd
    };

    setEditedText(e.target.value);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey && !multiline) {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditedText(text);
      setIsEditing(false);
    }
  };

  const renderLatexContent = content => {
    if (!content) {
      return null;
    }

    // Process <VISUAL> tags first
    const visualComponents = [];
    let processedText = content;

    // Extract <VISUAL> tags
    const visualRegex = /<VISUAL>([\s\S]*?)<\/VISUAL>/g;
    let visualMatch;
    let visualIndex = 0;
    let offset = 0;

    while ((visualMatch = visualRegex.exec(content)) !== null) {
      const placeholder = `__VISUAL_${visualIndex}__`;
      const beforeVisual = processedText.substring(0, visualMatch.index - offset);
      const afterVisual = processedText.substring(
        visualMatch.index + visualMatch[0].length - offset
      );

      // Store visual component
      visualComponents.push({
        index: visualIndex,
        content: visualMatch[1]
      });

      // Replace visual tag with placeholder
      processedText = beforeVisual + placeholder + afterVisual;

      // Update offset for next replacement
      offset += visualMatch[0].length - placeholder.length;
      visualIndex++;
    }

    // Split content by code blocks first
    const segments = [];
    let currentIndex = 0;
    const codeBlockRegex = /```[\s\S]*?```/g;
    let match;

    while ((match = codeBlockRegex.exec(processedText)) !== null) {
      // Add text before code block
      if (match.index > currentIndex) {
        segments.push({
          type: 'text',
          content: processedText.slice(currentIndex, match.index)
        });
      }

      // Add code block
      segments.push({
        type: 'code',
        content: match[0].slice(3, -3) // Remove ``` from start and end
      });

      currentIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (currentIndex < processedText.length) {
      segments.push({
        type: 'text',
        content: processedText.slice(currentIndex)
      });
    }

    // Process each segment
    return segments.map((segment, index) => {
      if (segment.type === 'code') {
        return (
          <pre
            key={index}
            className="bg-gray-100 rounded-md p-2 my-2 font-mono text-sm whitespace-pre-wrap break-all"
            style={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}
          >
            {segment.content}
          </pre>
        );
      } else {
        // Process visual placeholders first
        const parts = [];
        let currentLine = segment.content;

        // Check for visual placeholders
        for (const visual of visualComponents) {
          const placeholder = `__VISUAL_${visual.index}__`;
          if (currentLine.includes(placeholder)) {
            const splitByVisual = currentLine.split(placeholder);

            // Process the parts before the visual
            if (splitByVisual[0]) {
              parts.push({
                type: 'text',
                content: splitByVisual[0]
              });
            }

            // Add visual
            parts.push({
              type: 'visual',
              content: visual.content
            });

            // Update current line to be the remainder
            currentLine = splitByVisual.slice(1).join('');
          }
        }

        // Add any remaining text
        if (currentLine) {
          parts.push({
            type: 'text',
            content: currentLine
          });
        }

        // Render the parts
        const renderedParts = parts.map((part, partIndex) => {
          if (part.type === 'visual') {
            return (
              <div
                key={`visual-${lineIndex}-${partIndex}`}
                className="my-2 p-3 border border-gray-300 rounded-md bg-gray-50"
              >
                <div className="flex items-center mb-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className="w-4 h-4 text-gray-500 mr-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <span className="text-xs font-medium text-gray-500">Visual Description</span>
                </div>
                <div className="text-gray-700 text-sm">{part.content.trim()}</div>
              </div>
            );
          } else {
            // Simply display the text content without any LaTeX processing
            return <span key={`text-${partIndex}`}>{part.content}</span>;
          }
        });

        return (
          <React.Fragment key={`${index}`}>
            {renderedParts}
          </React.Fragment>
        );
      }
    });
  };

  // Calculate minimum height based on content to maintain consistent height
  const getMinHeight = () => {
    const content = isEditing ? editedText : text;
    if (!content || typeof content !== 'string') {
      return multiline ? '8rem' : '3rem'; // Larger default minimum size
    }

    // For multiline text, use a larger base size by default
    if (multiline) {
      // Count explicit newlines, but also account for potential wrapping
      const explicitLineCount = (content.match(/\n/g) || []).length + 1;
      const textLength = content.length;

      // Estimate: assume characters per line is roughly 60 for wrapping purposes (reduced from 80)
      // Add estimated wrapped lines to explicit newlines
      const estimatedLines = Math.max(explicitLineCount, Math.ceil(textLength / 60));

      // Use a minimum of 8rem for multiline, but grow based on estimated lines with more generous spacing
      return `${Math.max(estimatedLines * 2, 8)}rem`;
    }

    // For single-line text, more generous height based on length
    const estimatedHeight = Math.max(3, Math.min(8, Math.ceil(content.length / 60)));
    return `${estimatedHeight}rem`;
  };

  const minHeight = getMinHeight();
  const formattedEditText = isEditing ? getFormattedEditText() : editedText;

  if (isEditing) {
    return multiline ? (
      <div className="relative">
        <textarea
          ref={inputRef}
          value={formattedEditText}
          className={`w-full p-3 rounded-md ${className}`}
          style={{
            minHeight,
            height: 'auto',
            transition: 'all 0.2s ease',
            boxShadow: 'none',
            outline: 'none',
            border: '2px solid #d1d5db', // More subtle gray border
            resize: 'vertical', // Allow vertical resizing
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 'none',
            fontSize: '1rem',
            lineHeight: '1.5',
            overflowY: 'auto', // Allow vertical scrolling if needed
            whiteSpace: 'pre-wrap', // Preserve line breaks but wrap text
            wordBreak: 'break-word' // Break words to prevent horizontal overflow
          }}
          placeholder={placeholder}
          rows="auto" // Let the browser handle row count automatically
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
        <div className="text-xs text-gray-500 mt-1">
          Visual descriptions can be edited in the format: [Visual Description: your description
          here]
        </div>
      </div>
    ) : (
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={formattedEditText}
          className={`w-full p-3 rounded-md ${className}`}
          style={{
            minHeight,
            height: 'auto',
            transition: 'all 0.2s ease',
            boxShadow: 'none',
            outline: 'none',
            border: '2px solid #d1d5db', // More subtle gray border
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 'none',
            fontSize: '1rem',
            lineHeight: '1.5',
            textOverflow: 'ellipsis' // Show ellipsis for overflowing text
          }}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
        <div className="text-xs text-gray-500 mt-1">
          Visual descriptions can be edited in the format: [Visual Description: your description
          here]
        </div>
      </div>
    );
  }

  return (
    <div
      className={`cursor-text hover:bg-gray-50 p-3 rounded-md ${className} ${!text ? 'text-gray-400' : ''}`}
      style={{
        minHeight,
        transition: 'all 0.2s ease',
        border: '1px solid transparent',
        width: '100%',
        boxSizing: 'border-box',
        fontSize: '1rem',
        lineHeight: '1.5',
        wordWrap: 'break-word',
        maxWidth: 'none',
        whiteSpace: 'pre-wrap', // Preserve formatting but allow wrapping
        overflowWrap: 'break-word' // Ensure long words wrap
      }}
      onClick={handleClick}
    >
      {text ? (renderLatex ? renderLatexContent(text) : text) : placeholder}
    </div>
  );
};

export default EditableText;
